import React, { useState, useEffect } from 'react';
import { FileText, Download } from 'lucide-react';
import { useViaggi, useContatti, useMagazzini } from '../hooks/useSupabase';
import { formatDate, formatCurrency } from '../utils/formatters';
import { exportToExcel, exportToPDF } from '../services/export';

const Report = () => {
  const { viaggi, fetchViaggiBetweenDates } = useViaggi();
  const { contatti } = useContatti();
  const { magazzini } = useMagazzini();
  
  const [reportPeriodo, setReportPeriodo] = useState('corrente');
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  const [usaIntervallo, setUsaIntervallo] = useState(false);
  const [viaggiFiltrati, setViaggiFiltrati] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Imposta le date iniziali
  useEffect(() => {
    const oggi = new Date();
    const unMeseFa = new Date();
    unMeseFa.setMonth(oggi.getMonth() - 1);
    
    setDataFine(oggi.toISOString().split('T')[0]);
    setDataInizio(unMeseFa.toISOString().split('T')[0]);
  }, []);
  
  // Funzione per formattare la data in YYYY-MM-DD
  const formatDateForDB = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Aggiorna i viaggi filtrati quando cambia il periodo
  useEffect(() => {
    setLoading(true);
    
    const fetchData = async () => {
      try {
        let viaggiFiltrati;
        
        if (usaIntervallo && dataInizio && dataFine) {
          viaggiFiltrati = await fetchViaggiBetweenDates(dataInizio, dataFine);
        } else {
          const oggi = new Date();
          
          switch (reportPeriodo) {
            case 'corrente': {
              // MESE CORRENTE: dal primo all'ultimo giorno del mese corrente
              const annoCorrente = oggi.getFullYear();
              const meseCorrente = oggi.getMonth();
              
              const primoGiorno = formatDateForDB(new Date(annoCorrente, meseCorrente, 1));
              const ultimoGiorno = formatDateForDB(new Date(annoCorrente, meseCorrente + 1, 0));
              
              console.log(`Mese corrente: dal ${primoGiorno} al ${ultimoGiorno}`);
              
              viaggiFiltrati = await fetchViaggiBetweenDates(primoGiorno, ultimoGiorno);
              break;
            }
            case 'precedente': {
              // MESE PRECEDENTE: dal primo all'ultimo giorno del mese precedente
              const annoCorrente = oggi.getFullYear();
              const mesePrecedente = oggi.getMonth() - 1;
              
              const primoGiorno = formatDateForDB(new Date(annoCorrente, mesePrecedente, 1));
              const ultimoGiorno = formatDateForDB(new Date(annoCorrente, mesePrecedente + 1, 0));
              
              console.log(`Mese precedente: dal ${primoGiorno} al ${ultimoGiorno}`);
              
              viaggiFiltrati = await fetchViaggiBetweenDates(primoGiorno, ultimoGiorno);
              break;
            }
            case 'anno': {
              // ANNO CORRENTE: dal primo gennaio all'oggi
              const annoCorrente = oggi.getFullYear();
              
              const primoGiorno = formatDateForDB(new Date(annoCorrente, 0, 1));
              const ultimoGiorno = formatDateForDB(oggi);
              
              console.log(`Anno corrente: dal ${primoGiorno} al ${ultimoGiorno}`);
              
              viaggiFiltrati = await fetchViaggiBetweenDates(primoGiorno, ultimoGiorno);
              break;
            }
            case 'tutti':
            default:
              viaggiFiltrati = viaggi;
          }
        }
        
        setViaggiFiltrati(viaggiFiltrati);
      } catch (error) {
        console.error('Errore nel recupero viaggi:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [reportPeriodo, usaIntervallo, dataInizio, dataFine, fetchViaggiBetweenDates, viaggi]);
  
  // Funzione per trovare il nome del contatto
  const trovaContatto = (id) => {
    const contatto = contatti.find(c => c.id === id);
    if (!contatto) return '';
    
    const nomeCognome = [contatto.nome, contatto.cognome].filter(Boolean).join(' ');
    if (contatto.azienda && nomeCognome) {
      return `${contatto.azienda} (${nomeCognome})`;
    }
    return contatto.azienda || nomeCognome || '';
  };
  
  // Funzione per trovare il nome del magazzino
  const trovaMagazzino = (id) => {
    const magazzino = magazzini.find(m => m.id === id);
    return magazzino ? magazzino.nome : '';
  };
  
  // Funzione per generare report Excel
  const handleEsportaExcel = () => {
    exportToExcel(viaggiFiltrati, contatti, magazzini, `report-viaggi-${usaIntervallo ? 'personalizzato' : reportPeriodo}`);
  };
  
  // Funzione per generare report PDF
  const handleEsportaPDF = () => {
    exportToPDF(viaggiFiltrati, contatti, magazzini, `report-viaggi-${usaIntervallo ? 'personalizzato' : reportPeriodo}`);
  };
  
  // Calcolo totali
  const totalPrezzoCliente = viaggiFiltrati.reduce((sum, viaggio) => sum + Number(viaggio.prezzoCliente || 0), 0);
  const totalPrezzoVettore = viaggiFiltrati.reduce((sum, viaggio) => sum + Number(viaggio.prezzoVettore || 0), 0);
  const totalBancali = viaggiFiltrati.reduce((sum, viaggio) => sum + Number(viaggio.bancali || 0), 0);

  // Funzione per formattare l'ID in modo più leggibile
  const formatUUID = (uuid) => {
    if (!uuid) return '';
    // Prendi solo gli ultimi 4 caratteri dell'UUID per maggiore leggibilità
    return uuid.substring(uuid.length - 4);
  };

  return (
    <>
      {/* Box per i filtri e i pulsanti di esportazione */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-semibold mb-4">Filtri Report</h2>
        
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap gap-12 items-start">
            <div className="w-64">
              <label className="flex items-center mb-2">
                <input
                  type="radio"
                  className="form-radio h-5 w-5 text-blue-600"
                  checked={!usaIntervallo}
                  onChange={() => setUsaIntervallo(false)}
                />
                <span className="ml-2 font-medium">Periodo predefinito</span>
              </label>
              
              <select 
                className="w-full p-2 border rounded"
                value={reportPeriodo}
                onChange={e => setReportPeriodo(e.target.value)}
                disabled={usaIntervallo}
              >
                <option value="corrente">Mese Corrente</option>
                <option value="precedente">Mese Precedente</option>
                <option value="anno">Anno Corrente</option>
                <option value="tutti">Tutti i Viaggi</option>
              </select>
            </div>
            
            <div className="w-auto">
              <label className="flex items-center mb-2">
                <input
                  type="radio"
                  className="form-radio h-5 w-5 text-blue-600"
                  checked={usaIntervallo}
                  onChange={() => setUsaIntervallo(true)}
                />
                <span className="ml-2 font-medium">Intervallo personalizzato</span>
              </label>
              
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-gray-700 whitespace-nowrap">Data inizio:</label>
                  <input 
                    type="date" 
                    className="p-2 border rounded"
                    value={dataInizio}
                    onChange={e => setDataInizio(e.target.value)}
                    disabled={!usaIntervallo}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-gray-700 whitespace-nowrap">Data fine:</label>
                  <input 
                    type="date" 
                    className="p-2 border rounded"
                    value={dataFine}
                    onChange={e => setDataFine(e.target.value)}
                    disabled={!usaIntervallo}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button 
            className="bg-green-600 text-white px-4 py-2 rounded inline-flex items-center"
            onClick={handleEsportaExcel}
            disabled={loading || viaggiFiltrati.length === 0}
          >
            <Download size={20} className="mr-2" />
            Esporta Excel
          </button>
          
          <button 
            className="bg-red-600 text-white px-4 py-2 rounded inline-flex items-center"
            onClick={handleEsportaPDF}
            disabled={loading || viaggiFiltrati.length === 0}
          >
            <FileText size={20} className="mr-2" />
            Esporta PDF
          </button>
        </div>
      </div>
      
      {/* Box separato per l'anteprima del report */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Anteprima Report</h2>
          <span className="text-gray-600 font-medium">
            Totale viaggi periodo selezionato: {viaggiFiltrati.length}
          </span>
        </div>
        
        {loading ? (
          <div className="text-center py-4">Caricamento in corso...</div>
        ) : viaggiFiltrati.length === 0 ? (
          <p className="text-gray-500">Nessun viaggio nel periodo selezionato</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-2 text-center w-16">ID</th>
                  <th className="py-2 px-2 text-left w-24">Data</th>
                  <th className="py-2 px-2 text-left w-56">Cliente</th>
                  <th className="py-2 px-2 text-left w-56">Vettore</th>
                  <th className="py-2 px-2 text-right w-28">Prezzo C.</th>
                  <th className="py-2 px-2 text-right w-28">Prezzo V.</th>
                  <th className="py-2 px-2 text-right w-16">PLT</th>
                  <th className="py-2 px-2 text-left w-40">Carico</th>
                  <th className="py-2 px-2 text-left w-40">Scarico</th>
                </tr>
              </thead>
              <tbody>
                {viaggiFiltrati.map((viaggio) => (
                  <tr key={viaggio.id} className="border-t hover:bg-gray-50">
                    <td className="py-2 px-2 text-center">{formatUUID(viaggio.id)}</td>
                    <td className="py-2 px-2">{formatDate(viaggio.data)}</td>
                    <td className="py-2 px-2 truncate">{trovaContatto(viaggio.clienteId)}</td>
                    <td className="py-2 px-2 truncate">{trovaContatto(viaggio.vettoreId)}</td>
                    <td className="py-2 px-2 text-right">{formatCurrency(viaggio.prezzoCliente)}</td>
                    <td className="py-2 px-2 text-right">{formatCurrency(viaggio.prezzoVettore)}</td>
                    <td className="py-2 px-2 text-right">{viaggio.bancali}</td>
                    <td className="py-2 px-2 truncate">{trovaMagazzino(viaggio.magazzinoCarico)}</td>
                    <td className="py-2 px-2 truncate">{trovaMagazzino(viaggio.magazzinoScarico)}</td>
                  </tr>
                ))}
                
                {/* Riga totale */}
                <tr className="border-t bg-gray-100 font-semibold">
                  <td colSpan={4} className="py-2 px-2 text-right">TOTALE:</td>
                  <td className="py-2 px-2 text-right">{formatCurrency(totalPrezzoCliente)}</td>
                  <td className="py-2 px-2 text-right">{formatCurrency(totalPrezzoVettore)}</td>
                  <td className="py-2 px-2 text-right">{totalBancali}</td>
                  <td colSpan={2} className="py-2 px-2"></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default Report;